import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { COMMIT_SHA, BUILD_SOURCE } from '../constants/build';

const API = process.env.REACT_APP_API_URL || '';

// ─────────────────────────────────────────────────────────────────────────────
// Internal "Report a bug / Request a feature".
//
// ⚠️ MOUNTED IN AdminLayout, so it is on EVERY admin page. That is the point:
// the moment someone hits a defect is the moment they will report it, and a
// control that lives on one settings page is a control nobody reaches while
// looking at the broken screen.
//
// ⚠️ THE CONTEXT IS READ HERE, NOT ASKED FOR. url, route and viewport come from
// the browser at the moment the modal opens. Asking a reporter to paste a URL is
// how you get bug reports with no URL.
//
// ⚠️ NO SCREENSHOT. Deliberately: a screenshot of an admin page contains a
// borrower's file, and capturing one silently turns a bug report into a
// personal-data record. That is a bigger decision than this control, and it is
// not made by default.
// ─────────────────────────────────────────────────────────────────────────────
export default function ReportIssue() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState('bug');
  const [summary, setSummary] = useState('');
  const [shot, setShot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState('');

  // ── HOW THIS OPENS NOW ──────────────────────────────────────────────────
  // ⚠️ THE FLOATING BUTTON IS GONE, AND THE REACH IT GAVE IS NOT. The note at
  // the top of this file argues the control must be available at the moment
  // someone hits a defect, and it is right — requiring the debrief panel to be
  // opened first would be a real loss, not a tidy-up.
  //
  // So this component no longer owns a trigger. It listens for a `doorly:report`
  // event and opens on the requested kind. TWO things dispatch it, and neither
  // needs the panel open:
  //   · the debrief widget's FOOTER — "Report a bug" / "Request a feature"
  //   · the LAUNCHER's second segment — one tap from any admin page, straight
  //     into report mode without the panel ever rendering
  //
  // ⚠️ A HIDDEN GESTURE WAS THE OBVIOUS ALTERNATIVE AND IS THE WRONG ANSWER.
  // Right-click, long-press or a keyboard chord all keep the reach on paper and
  // lose it in practice: a control you must already know about is not available
  // to the person who just hit a defect, which is precisely the population this
  // exists for. The launcher segment is visible, so it costs one tap and no
  // knowledge — one floating element instead of two, with nothing traded away.
  useEffect(() => {
    const onAsk = (e) => {
      const k = e?.detail?.kind;
      if (k === 'bug' || k === 'feature') setKind(k);
      setOpen(true);
    };
    window.addEventListener('doorly:report', onAsk);
    return () => window.removeEventListener('doorly:report', onAsk);
  }, []);

  const token = localStorage.getItem('doorly_admin_token');
  if (!token) return null;

  if (!open) return null;

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      // ⚠️ MULTIPART ALWAYS, EVEN WITH NO FILE. One request shape means the
      // server parses the body the same way whether or not an image came with
      // it — a JSON path and a multipart path would be two places for the
      // context allow-list to drift.
      const fd = new FormData();
      fd.append('kind', kind);
      fd.append('summary', summary);
      // ⚠️ CAPTURED, NOT TYPED. The server allow-lists and truncates these,
      // and stamps the reporter and timestamp itself.
      fd.append('context', JSON.stringify({
        url: window.location.href,
        route: location.pathname + (location.search || ''),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        referrer: document.referrer || null,
        // ⚠️ THE BUILD THE REPORT WAS FILED FROM, CAPTURED NOT ASKED. This is
        // the highest-value placement of the three: it answers "was this
        // reported against a build containing the fix?" automatically, on every
        // report, without anyone remembering to check. Three times this session
        // a fix was discussed against the wrong artefact — that question now
        // arrives already answered.
        build_commit: COMMIT_SHA,
        build_source: BUILD_SOURCE,
      }));
      if (shot) fd.append('screenshot', shot);
      const r = await axios.post(`${API}/api/admin/report`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDone(r.data?.ticket_number || 'filed');
      // A rejected image must not read as a rejected report.
      if (r.data?.attachment_warning) setErr(r.data.attachment_warning);
      setSummary(''); setShot(null);
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not file the report.');
    }
    setBusy(false);
  };

  const close = () => { setOpen(false); setDone(null); setErr(''); setShot(null); };


  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(17,24,39,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 480, padding: 20, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        {done ? (
          <>
            <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: '#111827' }}>Thanks — filed as {done}</h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>
              It is owned by Engineering and will appear in the Tickets hub.
            </p>
            <button onClick={close} style={btnPrimary}>Close</button>
          </>
        ) : (
          <>
            <h3 style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 700, color: '#111827' }}>Report an issue</h3>

            {/* ⚠️ AN EXPLICIT CHOICE, NOT A GUESS FROM THE TEXT. "It keeps
                logging me out" and "it would be good if it remembered me" are
                different queues, and only the person writing it knows which. */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              {[['bug', 'Something is broken'], ['feature', 'Request a feature']].map(([k, label]) => (
                <button key={k} onClick={() => setKind(k)} style={{
                  flex: 1, padding: '9px 10px', borderRadius: 9, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  border: '1px solid ' + (kind === k ? '#3a6ad9' : '#e5e7eb'),
                  background: kind === k ? '#eef2fb' : '#fff', color: kind === k ? '#3a6ad9' : '#374151',
                }}>{label}</button>
              ))}
            </div>

            {/* ⚠️ autoFocus, AND IT IS THE LIKELY ROOT CAUSE RATHER THAN A
                POLISH ITEM. This modal has exactly ONE text field, and nothing
                focused it. A modal that opens with a single input and no focus
                invites the reporter to start typing into a page that is not
                listening — the box stays empty, Send stays disabled, and the
                control reads as broken. The gate logic was never the problem,
                and hint text alone could not fix it: a message about how many
                characters are needed is no help to someone whose characters are
                going nowhere.

                ⚠️ SAFE ON A SINGLE-FIELD MODAL. Autofocus is a real
                accessibility hazard on a form with several inputs — it moves a
                screen-reader user past context they have not heard. With one
                field under a heading, focusing it IS the context. */}
            <textarea
              autoFocus
              aria-label={kind === 'bug' ? 'Describe what happened' : 'Describe what you would like'}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={5}
              placeholder={kind === 'bug'
                ? 'What were you doing, and what happened instead?'
                : 'What would you like to be able to do?'}
              style={{ width: '100%', padding: 10, border: '1px solid #e5e7eb', borderRadius: 9, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
            />

            {/* ── attachment ─────────────────────────────────────────────
                ⚠️ A DELIBERATE UPLOAD, NOT A SILENT CAPTURE — see the note at
                the top of this file and the report_attachments block in
                db/database.js. Offered on bugs only: a feature request has no
                broken screen to photograph. */}
            {kind === 'bug' && (
              <div style={{ margin: '10px 0 4px' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, color: '#3a6ad9', fontWeight: 600 }}>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => { setShot(e.target.files?.[0] || null); setErr(''); }}
                    style={{ display: 'none' }}
                  />
                  {shot ? 'Change screenshot' : 'Attach a screenshot (optional)'}
                </label>
                {shot && (
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>{shot.name} ({Math.round(shot.size / 1024)} KB)</span>
                    <button onClick={() => setShot(null)} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 11, textDecoration: 'underline', padding: 0 }}>remove</button>
                  </div>
                )}
                {/* ⚠️ SAID PLAINLY, BECAUSE THE REPORTER IS THE CONTROL. Nothing
                    inspects the image for borrower data; the person choosing it
                    is who decides, and they can only decide if they are told. */}
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6, lineHeight: 1.6 }}>
                  Images only, up to 10 MB. If the screen shows a borrower's details, they'll be stored with this report — attach it only if it's needed to reproduce the problem.
                </div>
              </div>
            )}

            {/* ⚠️ REASSURANCE, NOT AN INVENTORY. This listed the fields collected
                — page, screen size, browser, name — which answers a question
                nobody filing a bug is asking. What they need to know is that
                they don't have to describe any of it. Removed entirely for
                feature requests, where none of it is relevant. */}
            {kind === 'bug' && (
              <div style={{ fontSize: 11, color: '#9ca3af', margin: '10px 0 14px', lineHeight: 1.6 }}>
                We'll capture what page you were on and your session details, so you don't need to describe them.
              </div>
            )}
            {kind === 'feature' && <div style={{ height: 10 }} />}

            {err && <div style={{ marginBottom: 12, padding: '8px 10px', background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: 8, fontSize: 12, color: '#ff082f' }}>{err}</div>}

            {/* ⚠️ THE FLOOR IS NOW VISIBLE, AND THAT IS THE ACTUAL BUG FIX.
                Send is gated on summary.trim().length >= 10 — deliberately, "a
                floor, not a ceremony": "broken" is not a bug report. But the
                gate said NOTHING. A button that stays grey with no explanation
                is indistinguishable from a broken one, and it was duly reported
                as "the send button doesn't activate".

                Reproduced before changing anything, with the component mounted
                and typed into: the condition works correctly in BOTH modes, and
                flips at exactly ten characters. Nothing about the file input or
                the FormData change touches it. The defect was never the
                condition — it was that the condition was unexplained. */}
            {!busy && summary.trim().length < 10 && (
              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 8, textAlign: 'right' }}>
                {summary.trim().length === 0
                  ? 'Describe what happened to enable Send.'
                  : `${10 - summary.trim().length} more character${10 - summary.trim().length === 1 ? '' : 's'} to enable Send.`}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={close} style={btnGhost}>Cancel</button>
              <button
                onClick={submit}
                disabled={busy || summary.trim().length < 10}
                title={summary.trim().length < 10 ? 'Add a sentence or two first — at least 10 characters.' : undefined}
                style={{ ...btnPrimary, opacity: busy || summary.trim().length < 10 ? 0.5 : 1,
                  cursor: busy || summary.trim().length < 10 ? 'not-allowed' : 'pointer' }}>
                {busy ? 'Filing…' : 'Send'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const btnPrimary = { padding: '9px 16px', borderRadius: 9, border: 'none', background: '#3a6ad9', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const btnGhost = { padding: '9px 16px', borderRadius: 9, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
